import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import CategoryDetailsTab from "./CategoryDetailsTab";
import CategoryVisibility from "./CategoryVisibility";
import CategoryProductsTab from "./CategoryProductsTab";
import { createDSOKCategory, getDSOKCategory } from "app/services/api/category";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { useTranslation } from "react-i18next";
import { Box, Breadcrumbs, Tabs, Tab } from "@mui/material";
import { Link } from "react-router-dom";
import { Typography } from "@mui/material";
import Div from "@jumbo/shared/Div";
import {
  getDSOKCategories,
  getDSOKProducts,
  upsertDSOKCategory,
} from "app/services/api/management";

import { useSnackbar } from "notistack";
import CategoryCrossSellTab from "./CategoryCrossSellTab";
import CategoryBindingsTab from "./CategoryBindingsTab";
import CategoryPromotions from "./CategoryPromotions";

const DsokEditCategory = () => {
  const { categoryId } = useParams();
  const { t, i18n } = useTranslation();
  const { getAuthUser } = useJumboAuth();
  const { customer } = getAuthUser();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const defaultLanguage =
    customer?.dsokSettings?.customer?.defaultLanguage || i18n.language;
  const languages = customer?.dsokSettings?.customer?.languages || [];
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState("details");
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const crosssellActive =
    customer?.dsokSettings?.customer?.settings?.enableCrossSell ||
    customer?.dsokType === "gastrokiosk";
  const aiCategoryImageGenerationActive =
    customer?.dsokSettings?.customer?.customerUISettings
      ?.enableAiCategoryImageGeneration || false;

  // From UI Settings: Základné → "Povoliť špeciálne ponuky"; Produkty → "Povoliť väzby"
  const isSpecialOffersEnabled =
    customer?.dsokSettings?.customer?.customerUISettings?.enableSpecialOffers === true;
  const areBindingsEnabled =
    customer?.dsokSettings?.customer?.customerUISettings?.enableBindings === true;

  const currencyCode =
    customer?.dsokSettings?.customer?.currencyCode || "";

  useEffect(() => {
    if (categoryId !== "new") {
      loadCategory(categoryId);
    }
  }, [categoryId]);

  // Ensure activeTab is always one of the available tabs
  useEffect(() => {
    const available = ["details", "visibility"];
    if (categoryId !== "new") {
      available.push("products");
      if (isSpecialOffersEnabled) available.push("promotions");
      if (areBindingsEnabled) available.push("bindings");
    }
    if (crosssellActive && categoryId !== "new") {
      available.push("crosssell");
    }
    if (!available.includes(activeTab)) {
      setActiveTab(available[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, crosssellActive, isSpecialOffersEnabled, areBindingsEnabled, category]);

  const handleTabChange = (event, newValue) => {
    // For new categories, only allow the details tab
    if (categoryId === "new" && newValue !== "details") {
      setActiveTab("details");
    } else {
      setActiveTab(newValue);
    }
  };

  const loadCategory = async (categoryId) => {
    setLoading(true);
    try {
      const response = await getDSOKCategory(categoryId);
      const products = await getDSOKProducts();

      // Convert products array to object for easier lookup
      const productsMap = {};
      if (products && Array.isArray(products)) {
        products.forEach((product) => {
          productsMap[product._id] = product;
        });
      }

      // Map category products to full product objects
      let categoryWithProducts = response;

      if (response?.products) {
        let productsToMap = response.products;

        // Handle case where products is an array (from API)
        if (Array.isArray(productsToMap)) {
          const productsObject = {};
          productsToMap.forEach((productId) => {
            if (productsMap[productId]) {
              productsObject[productId] = productsMap[productId];
            }
          });
          categoryWithProducts = {
            ...response,
            products: productsObject,
          };
        } else {
          // Handle case where products is a corrupted object (character-by-character split)
          if (
            typeof response.products === "object" &&
            response.products["0"] &&
            response.products["1"]
          ) {
            // Try to reconstruct the JSON string from the corrupted object
            const jsonString = Object.values(response.products).join("");
            try {
              productsToMap = JSON.parse(jsonString);
            } catch (e) {
              console.error("Failed to parse corrupted products object:", e);
              productsToMap = {};
            }
          }

          // Handle case where products is a JSON string
          if (typeof productsToMap === "string") {
            try {
              productsToMap = JSON.parse(productsToMap);
            } catch (e) {
              console.error("Failed to parse products string:", e);
              productsToMap = {};
            }
          }

          // Now map the products to full product objects

          if (typeof productsToMap === "object" && productsToMap !== null) {
            const mappedProducts = {};
            Object.keys(productsToMap).forEach((productId) => {
              if (productsMap[productId]) {
                mappedProducts[productId] = productsMap[productId];
              }
            });
            categoryWithProducts = {
              ...response,
              products: mappedProducts,
            };
          } else {
            console.log(
              "LoadCategory - ProductsToMap is not an object or is null"
            );
          }
        }
      }
      // Normalize visibility if it comes as a JSON string or corrupted object
      if (response?.visibility) {
        let rawVisibility = response.visibility;
        if (
          typeof rawVisibility === "object" &&
          rawVisibility !== null &&
          rawVisibility["0"] &&
          rawVisibility["1"]
        ) {
          // Reconstruct JSON string from corrupted object
          const jsonString = Object.values(rawVisibility).join("");
          try {
            rawVisibility = JSON.parse(jsonString);
          } catch (e) {
            rawVisibility = {};
          }
        }
        if (typeof rawVisibility === "string") {
          try {
            rawVisibility = JSON.parse(rawVisibility);
          } catch (e) {
            rawVisibility = {};
          }
        }
        categoryWithProducts = {
          ...categoryWithProducts,
          visibility:
            rawVisibility && typeof rawVisibility === "object"
              ? rawVisibility
              : {},
        };
      }
      setProducts(products);
      await getDSOKCategories().then((categories) => {
        const categoryArr = Object.keys(categories || {}).map((id) => ({
          ...categories[id],
        }));
        setCategory(categoryWithProducts);
        setCategories(categoryArr);
      });
    } catch (error) {
      console.error("Error loading category:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = async (category) => {
    if (categoryId === "new") {
      try {
        delete category._id;

        await createDSOKCategory(category);
        const message = `${t("notifications.wasSuccessful")}`;
        enqueueSnackbar(message, {
          variant: "success",
        });
        navigate("/customer/dsok/categories");
      } catch (error) {
        console.error("Error creating category:", error);
        const message = `${t("notifications.wasFailed")}`;
        enqueueSnackbar(message, {
          variant: "error",
        });
      }
    } else {
      try {
        // Ensure products are in the correct format before saving
        let categoryToSave = { ...category };

        // Optimistic UI update so changes appear immediately
        setCategory(category);

        // Map AI image generator settings to top-level fields for backend compatibility
        if (
          categoryToSave.aiImage &&
          typeof categoryToSave.aiImage === "object"
        ) {
          const {
            category: aiCategory,
            locale: aiLocale,
            seed: aiSeed,
            style: aiStyle,
            negative_prompt: aiNegativePrompt,
          } = categoryToSave.aiImage;
          if (aiLocale !== undefined) categoryToSave.aiLocale = aiLocale;
          if (aiStyle !== undefined) categoryToSave.aiStyle = aiStyle;
          if (aiNegativePrompt !== undefined)
            categoryToSave.aiNegativePrompt = aiNegativePrompt;
          if (aiSeed !== undefined) categoryToSave.aiSeed = aiSeed;
          if (aiCategory !== undefined) categoryToSave.aiCategory = aiCategory;
        }

        // If products is an object with product objects, convert to just product IDs
        if (category.products && typeof category.products === "object") {
          const productIds = [];
          Object.keys(category.products).forEach((productId) => {
            if (
              category.products[productId] &&
              typeof category.products[productId] === "object"
            ) {
              productIds.push(productId); // Just store the ID
            }
          });
          categoryToSave.products = productIds;
        }
        if (category.promotions && typeof category.promotions === "object") {
          const promotionIds = [];
          Object.keys(category.promotions).forEach((promotionId) => {
            if (
              category.promotions[promotionId] &&
              typeof category.promotions[promotionId] === "object"
            ) {
              promotionIds.push(promotionId);
            }
          });
          categoryToSave.promotions = promotionIds;
        }

        await upsertDSOKCategory(categoryToSave);
        await loadCategory(categoryId);
        const message = `${t("notifications.wasSuccessful")}`;
        enqueueSnackbar(message, {
          variant: "success",
        });
      } catch (error) {
        console.error("Error saving category:", error);
        const message = `${t("notifications.wasFailed")}`;
        enqueueSnackbar(message, {
          variant: "error",
        });
      }
    }
  };

  return (
    <React.Fragment>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <Link to="/customer/dsok/categories">
          {t("sidebar.menuItem.dsokCategories")}
        </Link>
        <Typography color="text.primary">
          {category?.name[defaultLanguage]}
        </Typography>
      </Breadcrumbs>
      <Div
        sx={{
          display: "flex",
          width: "100%",
          p: 1,
        }}
      >
        <Box
          sx={{
            width: "100%",
            backgroundColor: "white",
            borderRadius: 1,
            boxShadow: 1,
            p: 2,
          }}
        >
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="category tabs"
            >
              <Tab
                label={t("pages.dsokCategories.details")}
                id="category-tab-details"
                aria-controls="category-tabpanel-details"
                value="details"
              />
              <Tab
                label={t("pages.dsokCategories.visibility", {
                  defaultValue: "Visibility",
                })}
                id="category-tab-visibility"
                aria-controls="category-tabpanel-visibility"
                value="visibility"
              />
              {categoryId !== "new" && isSpecialOffersEnabled && (
                <Tab
                  label={t("pages.dsokPromotions.title", {
                    defaultValue: "Promotions",
                  })}
                  id="category-tab-promotions"
                  aria-controls="category-tabpanel-promotions"
                  value="promotions"
                />
              )}
              {categoryId !== "new" && (
                <Tab
                  label={t("pages.dsokCategories.products")}
                  id="category-tab-products"
                  aria-controls="category-tabpanel-products"
                  value="products"
                />
              )}
              {categoryId !== "new" && areBindingsEnabled && (
                <Tab
                  label={t("pages.dsokCategories.bindings")}
                  id="category-tab-bindings"
                  aria-controls="category-tabpanel-bindings"
                  value="bindings"
                />
              )}
              {categoryId !== "new" && crosssellActive && (
                <Tab
                  label={t("pages.dsokCategories.crosssell")}
                  id="category-tab-crosssell"
                  aria-controls="category-tabpanel-crosssell"
                  value="crosssell"
                />
              )}
            </Tabs>
          </Box>

          {/* Tab Panels */}
          <Box
            role="tabpanel"
            hidden={activeTab !== "details"}
            id="category-tabpanel-details"
            aria-labelledby="category-tab-details"
          >
            {activeTab === "details" && (
              <CategoryDetailsTab
                category={category}
                languages={languages}
                defaultLanguage={defaultLanguage}
                onEditCategory={handleEditCategory}
                categories={categories}
                loading={loading}
                aiCategoryImageGenerationActive={
                  aiCategoryImageGenerationActive
                }
              />
            )}
          </Box>

          <Box
            role="tabpanel"
            hidden={activeTab !== "visibility"}
            id="category-tabpanel-visibility"
            aria-labelledby="category-tab-visibility"
          >
            {activeTab === "visibility" && (
              <CategoryVisibility
                category={category}
                onEditCategory={handleEditCategory}
                loading={loading}
              />
            )}
          </Box>

          {categoryId !== "new" && isSpecialOffersEnabled && (
            <Box
              role="tabpanel"
              hidden={activeTab !== "promotions"}
              id="category-tabpanel-promotions"
              aria-labelledby="category-tab-promotions"
            >
              {activeTab === "promotions" && (
                <CategoryPromotions
                  category={category}
                  onEditCategory={handleEditCategory}
                  defaultLanguage={defaultLanguage}
                />
              )}
            </Box>
          )}

          {categoryId !== "new" && (
            <Box
              role="tabpanel"
              hidden={activeTab !== "products"}
              id="category-tabpanel-products"
              aria-labelledby="category-tab-products"
            >
              {activeTab === "products" && (
                <CategoryProductsTab
                  category={category}
                  products={products}
                  onEditCategory={handleEditCategory}
                  defaultLanguage={defaultLanguage}
                  currencyCode={currencyCode}
                />
              )}
            </Box>
          )}
          {categoryId !== "new" && areBindingsEnabled && (
            <Box
              role="tabpanel"
              hidden={activeTab !== "bindings"}
              id="category-tabpanel-bindings"
              aria-labelledby="category-tab-bindings"
            >
              {activeTab === "bindings" && (
                <CategoryBindingsTab
                  category={category}
                  products={products}
                  onEditCategory={handleEditCategory}
                  defaultLanguage={defaultLanguage}
                />
              )}
            </Box>
          )}
          {crosssellActive && categoryId !== "new" && (
            <Box
              role="tabpanel"
              hidden={activeTab !== "crosssell"}
              id="category-tabpanel-crosssell"
              aria-labelledby="category-tab-crosssell"
            >
              {activeTab === "crosssell" && (
                <CategoryCrossSellTab category={category} products={products} />
              )}
            </Box>
          )}
        </Box>
      </Div>
    </React.Fragment>
  );
};

export default DsokEditCategory;
